import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, GraduationCap, LayoutDashboard, ListRestart, LogOut, RefreshCw, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorState, Loading } from "@/components/States";
import { Button } from "@/components/ui/button";
import { AdminTabContent } from "@/pages/admin/AdminTabs";

const TABS = ["Resumen", "Familias", "Estudiantes", "Jobs", "Planes"];
const TAB_META = {
  Resumen: { icon: LayoutDashboard, shortLabel: "Resumen", tone: "bg-blue-500/20 text-blue-300" },
  Familias: { icon: Users, shortLabel: "Familias", tone: "bg-cyan-500/20 text-cyan-300" },
  Estudiantes: { icon: GraduationCap, shortLabel: "Alumnos", tone: "bg-violet-500/20 text-violet-300" },
  Jobs: { icon: ListRestart, shortLabel: "Jobs", tone: "bg-amber-500/20 text-amber-300" },
  Planes: { icon: CreditCard, shortLabel: "Planes", tone: "bg-emerald-500/20 text-emerald-300" },
};

export default function AdminPanel() {
  const { logout, principal } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Resumen");
  const [overview, setOverview] = useState(null);
  const [families, setFamilies] = useState(null);
  const [students, setStudents] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [jobFilter, setJobFilter] = useState("");
  const [subs, setSubs] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [o, f, s, j, sub] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/families"),
        api.get("/admin/students"),
        api.get("/admin/jobs", { params: jobFilter ? { job_status: jobFilter } : {} }),
        api.get("/admin/subscriptions"),
      ]);
      setOverview(o.data);
      setFamilies(f.data);
      setStudents(s.data);
      setJobs(j.data);
      setSubs(sub.data);
    } catch (err) {
      setError(apiError(err, "No pudimos cargar el panel de administracion."));
    }
  }, [jobFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const retryJob = async (id) => {
    try {
      await api.post(`/admin/jobs/${id}/retry`);
      toast.success("Job reencolado");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (error) return <div className="p-8"><ErrorState message={error} onRetry={load} /></div>;
  if (!overview) return <Loading label="Cargando operaciones..." />;

  return (
    <div className="min-h-screen bg-slate-900 pb-28 text-slate-100" data-testid="admin-panel">
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-4 sm:px-6">
          <span className="font-display text-lg font-bold">MAIKY · Admin</span>
          <span className="hidden text-sm text-slate-400 sm:inline">{principal?.email}</span>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="icon" onClick={load} data-testid="admin-refresh-btn" aria-label="Refrescar">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="admin-logout-btn"
              aria-label="Salir"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 pb-3 sm:px-6" aria-label="Navegación principal de administración">
          {TABS.map((item) => (
            <button
              key={item}
              onClick={() => setTab(item)}
              className={`estudia-touch shrink-0 rounded-xl px-4 text-sm font-semibold transition-colors ${
                tab === item ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800"
              }`}
              data-testid={`admin-tab-${item.toLowerCase()}`}
            >
              {item}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <AdminTabContent
          tab={tab}
          overview={overview}
          families={families}
          students={students}
          jobs={jobs}
          jobFilter={jobFilter}
          onFilter={setJobFilter}
          onRetry={retryJob}
          subscriptions={subs}
        />
      </main>

      <AdminBottomNav tab={tab} onSelect={setTab} />
    </div>
  );
}
function AdminBottomNav({ tab, onSelect }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-700 bg-slate-950/95 px-2 shadow-[0_-8px_28px_rgba(0,0,0,0.28)] backdrop-blur-xl"
      aria-label="Accesos rápidos de administración"
      data-testid="admin-bottom-nav"
    >
      <div className="mx-auto flex max-w-3xl">
        {TABS.map((item) => {
          const Icon = TAB_META[item].icon;
          return (
            <button
              key={item}
              type="button"
              onClick={() => onSelect(item)}
              className={`my-2 flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-2 text-[11px] font-bold transition sm:text-xs ${
                tab === item ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
              data-testid={`admin-bottom-${item.toLowerCase()}`}
            >
              <span className={`grid h-9 w-9 place-items-center rounded-xl shadow-sm ${TAB_META[item].tone}`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="max-w-full truncate">{TAB_META[item].shortLabel}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
